import { AnimatePresence, motion } from 'framer-motion';
import { X } from '../components/icons';
import { useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { cn } from '../utils/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
  fullscreen?: boolean;
}

const Presence = AnimatePresence as ComponentType<{ children?: ReactNode }>;

export default function Modal({ open, onClose, title, children, className, fullscreen = false }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <Presence>
      {open ? (
        <motion.div className={cn('fixed inset-0 z-[110] overflow-y-auto p-4', fullscreen && 'p-0')} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <button type="button" aria-label="Fechar" className="absolute inset-0 bg-black/60" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10 }}
            className={cn(
              'relative z-10 mx-auto my-6 flex max-h-[calc(100dvh-3rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-borderLight bg-cardLight p-5 shadow-2xl dark:border-borderDark dark:bg-cardDark',
              fullscreen && 'mx-0 my-0 h-[100dvh] max-h-[100dvh] max-w-none rounded-none border-0 p-0',
              className
            )}
          >
            <div
              className={cn(
                'mb-3 flex shrink-0 items-center justify-between gap-3',
                fullscreen && 'sticky top-0 z-20 mb-0 border-b border-borderLight bg-cardLight px-5 py-4 dark:border-borderDark dark:bg-cardDark'
              )}
            >
              {title ? <h3 className="text-lg font-bold">{title}</h3> : <span />}
              <button
                type="button"
                aria-label="Fechar modal"
                onClick={onClose}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-borderLight bg-white text-slate-700 transition hover:bg-slate-100 dark:border-borderDark dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className={cn('min-h-0 overflow-y-auto pr-1', fullscreen && 'px-5 py-4')}>{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </Presence>
  );
}
